import { useCallback, useEffect, useState } from "react";
import { LineChart, Scale, PieChart, CalendarClock, TrendingDown, Landmark, MessageSquareText } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { api } from "@/lib/api";
import { gbp2 as gbp, finBadge, monthLabel } from "@/data/financeOptions";

const today = () => new Date().toISOString().slice(0, 10);

const Bar = ({ value, max, negative }) => (
  <div className="h-2 w-full overflow-hidden rounded-full bg-[#E4E9F3]">
    <div className={`h-full rounded-full ${negative ? "bg-[#DC2626]" : "bg-[#C9A227]"}`} style={{ width: `${max ? Math.min(100, Math.abs(value) / max * 100) : 0}%` }} />
  </div>
);

export const FinanceOverview = ({ refreshKey }) => {
  const [s, setS] = useState(null);
  const load = useCallback(async () => { try { setS(await api.list("finance-summary")); } catch { setS(null); } }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const mcp = s?.monthly_cash_position ?? [];
  const cats = s?.spending_by_category ?? [];
  const upcoming = s?.upcoming_payments ?? [];
  const largest = s?.largest_recent_costs ?? [];
  const funding = s?.funding_summary ?? [];
  const updates = s?.recent_updates ?? [];
  const maxFlow = Math.max(1, ...mcp.map((m) => Math.max(m.inflows, m.outflows)));
  const maxCat = Math.max(1, ...cats.map((c) => c.amount));
  const budgetMax = Math.max(1, s?.budget_planned ?? 0, (s?.budget_actual ?? 0) + (s?.budget_committed ?? 0));

  return (
    <div className="space-y-6" data-testid="finance-overview">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard testid="fin-ov-cashpos-card" title="Monthly Cash Position" subtitle="Realised inflows vs outflows">
          {mcp.length === 0 ? <EmptyState icon={LineChart} title="No cash movements yet" description="Paid and received transactions appear here." /> : (
            <ul className="space-y-4" data-testid="fin-ov-cashpos-list">{mcp.map((m) => (
              <li key={m.month}>
                <div className="mb-1 flex items-center justify-between text-sm"><span className="font-medium text-[#14245F]">{monthLabel(m.month)}</span><span className={m.net < 0 ? "font-semibold text-[#DC2626]" : "font-semibold text-[#16A34A]"}>{m.net < 0 ? "−" : "+"}{gbp(Math.abs(m.net))}</span></div>
                <div className="flex items-center gap-2 text-xs text-[#5A6B8C]"><span className="w-14">In {gbp(m.inflows)}</span><Bar value={m.inflows} max={maxFlow} /></div>
                <div className="mt-1 flex items-center gap-2 text-xs text-[#5A6B8C]"><span className="w-14">Out {gbp(m.outflows)}</span><Bar value={m.outflows} max={maxFlow} negative /></div>
              </li>
            ))}</ul>
          )}
        </SectionCard>

        <SectionCard testid="fin-ov-planvsactual-card" title="Planned vs Actual Spend" subtitle="Across all budgets">
          {(s?.budget_planned ?? 0) === 0 ? <EmptyState icon={Scale} title="No budgets yet" description="Add budgets to compare planned vs actual." /> : (
            <div className="space-y-4" data-testid="fin-ov-planvsactual">
              <div><div className="mb-1 flex justify-between text-sm"><span className="text-[#5A6B8C]">Planned</span><span className="font-medium text-[#14245F]">{gbp(s.budget_planned)}</span></div><Bar value={s.budget_planned} max={budgetMax} /></div>
              <div><div className="mb-1 flex justify-between text-sm"><span className="text-[#5A6B8C]">Actual (paid)</span><span className="font-medium text-[#14245F]">{gbp(s.budget_actual)}</span></div><Bar value={s.budget_actual} max={budgetMax} /></div>
              <div><div className="mb-1 flex justify-between text-sm"><span className="text-[#5A6B8C]">Committed</span><span className="font-medium text-[#14245F]">{gbp(s.budget_committed)}</span></div><Bar value={s.budget_committed} max={budgetMax} /></div>
              {s.budget_actual + s.budget_committed > s.budget_planned && <p className="text-sm font-semibold text-[#DC2626]">Overspend: {gbp(s.budget_actual + s.budget_committed - s.budget_planned)} over plan</p>}
            </div>
          )}
        </SectionCard>

        <SectionCard testid="fin-ov-category-card" title="Spending by Category" subtitle="Paid expenses">
          {cats.length === 0 ? <EmptyState icon={PieChart} title="No spend yet" description="Paid expenses grouped by category appear here." /> : (
            <ul className="space-y-3" data-testid="fin-ov-category-list">{cats.map((c) => (
              <li key={c.category}><div className="mb-1 flex justify-between text-sm"><span className="text-[#14245F]">{c.category}</span><span className="font-medium text-[#14245F]">{gbp(c.amount)}</span></div><Bar value={c.amount} max={maxCat} /></li>
            ))}</ul>
          )}
        </SectionCard>

        <SectionCard testid="fin-ov-upcoming-card" title="Upcoming Payments" subtitle="Planned & committed outflows">
          {upcoming.length === 0 ? <EmptyState icon={CalendarClock} title="Nothing due" description="Planned or committed payments appear here." /> : (
            <ul className="space-y-3" data-testid="fin-ov-upcoming-list">{upcoming.slice(0, 6).map((u) => (
              <li key={u.id} className="flex items-start justify-between gap-3 border-b border-[#E4E9F3]/60 pb-3 last:border-0 last:pb-0">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-[#14245F]">{u.description}</p><p className={`text-xs ${u.overdue ? "font-semibold text-[#DC2626]" : "text-[#5A6B8C]"}`}>{u.overdue ? "Overdue" : "Due"} {u.due_date}{u.payee ? ` · ${u.payee}` : ""}</p></div>
                <span className="shrink-0 text-sm font-medium text-[#14245F]">{gbp(u.amount)}</span>
              </li>
            ))}</ul>
          )}
        </SectionCard>

        <SectionCard testid="fin-ov-largest-card" title="Largest Recent Costs" subtitle="Top paid expenses">
          {largest.length === 0 ? <EmptyState icon={TrendingDown} title="No costs yet" description="Your largest paid expenses appear here." /> : (
            <ul className="space-y-3" data-testid="fin-ov-largest-list">{largest.map((l) => (
              <li key={l.id} className="flex items-start justify-between gap-3 border-b border-[#E4E9F3]/60 pb-3 last:border-0 last:pb-0">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-[#14245F]">{l.description}</p><p className="text-xs text-[#5A6B8C]">{l.category} · {l.date}</p></div>
                <span className="shrink-0 text-sm font-medium text-[#14245F]">{gbp(l.amount)}</span>
              </li>
            ))}</ul>
          )}
        </SectionCard>

        <SectionCard testid="fin-ov-funding-card" title="Funding Summary" subtitle="Sources & status">
          {funding.length === 0 ? <EmptyState icon={Landmark} title="No funding yet" description="Funding sources appear here." /> : (
            <ul className="space-y-3" data-testid="fin-ov-funding-list">{funding.map((f) => (
              <li key={f.id} className="flex items-start justify-between gap-3 border-b border-[#E4E9F3]/60 pb-3 last:border-0 last:pb-0">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-[#14245F]">{f.name}</p><p className="text-xs text-[#5A6B8C]">{f.funding_type} · received {gbp(f.amount_received)}{f.amount_outstanding ? ` · outstanding ${gbp(f.amount_outstanding)}` : ""}</p></div>
                <StatusBadge status={finBadge(f.status)} label={f.status} />
              </li>
            ))}</ul>
          )}
        </SectionCard>
      </div>

      <SectionCard testid="fin-ov-updates-card" title="Recent Finance Updates" subtitle="Latest activity logged">
        {updates.length === 0 ? <EmptyState icon={MessageSquareText} title="No updates" description="Finance updates appear here." /> : (
          <ul className="space-y-3" data-testid="fin-ov-updates-list">{updates.map((u) => (
            <li key={u.id} className="border-b border-[#E4E9F3]/60 pb-3 last:border-0 last:pb-0"><p className="truncate text-sm font-medium text-[#14245F]">{u.title}</p><p className="text-xs text-[#5A6B8C]">{u.author}{u.related_name ? ` · ${u.related_name}` : ""}</p></li>
          ))}</ul>
        )}
      </SectionCard>
    </div>
  );
};
